import React from "react";
import {Box} from "@mui/material";
import CustomCard from "../../../../../components/CustomCard";
import {BuildingModalIcon, IconOpenEmailCard} from "../../../../../components/buttons/IconButtons";
import FilesGallerySummary from '../../../../../components/PhotoPicker/FilesGallerySummary';
import {EMPTY_VALUES} from '../../../../../Apis/functions';

/**
 * RequestAccessSummary
 *
 * Component to display a summary of the QR request access form.
 * Shows visitor information, employee/host information, visit purpose,
 * and uploaded documents. Handles dynamic rendering of file cards with icons.
 *
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param {Function} t - Translation function from react-i18next
 * @param {Array} visitorList - Array of selected visitors
 * @param {Object} userData - Data of the current user (if visitorList is empty)
 * @param {Array} employeeList - Array of selected employees
 * @param {Object} requestAccessItem - Current request access item model
 * @param {Array} photoPickerFile - List of uploaded files (photoPickerFile objects)
 *
 * @returns {JSX.Element} The summary view of the request access form
 */

const RequestAccessSummary = ({ t, visitorList, userData, employeeList, requestAccessItem, photoPickerFile}) => {
    let name;
    let lastName;
    let secondLastName;
    let email;
    let phoneNumber;
    let documentId;

    if(visitorList.length > 0){
        name = visitorList[0].name;
        lastName = visitorList[0].firstLastName;
        secondLastName = visitorList[0].secondLastName;
        email = visitorList?.[0]?.email;
        phoneNumber = visitorList?.[0]?.phone;
        documentId = visitorList?.[0]?.documentId;
    }else{
        name = userData.name;
        lastName = userData.lastName;
        secondLastName = userData.secondLastName;
        email = userData.email;
        phoneNumber = userData.phoneNumber;
        documentId = userData.documentId;

    }

    return (
        <>
            <Box className='background-general-00 box-shadow-primary-900-opacity-10 p-24px border-radius-8px'>
                <div className={"border-bottom-primary-700 p-0"}>
                    <p className={"fs-24-to-18 ibl-font-w-600 font-color-primary-700"}>{ t("summary") }</p>
                </div>

                {/******* Visitor Information ***********/}

                <div className={"row mt-4"}>
                    <CustomCard
                        classCard={`background-shades-50 border-radius-8px`}
                        classContent={"p-0"}
                        classes={"p-0"}
                        children={
                            <div className={"row g-3 mb-3"}>
                                <div className={"border-bottom-shades-300 p-3 d-flex align-items-center"}>
                                    <div className={"mr-2"}><BuildingModalIcon width={24} height={24}/></div>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("visitor_information")}</p>
                                </div>
                                <div className={"col-12"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("name")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ name || EMPTY_VALUES.DEFAULT }</p>
                                </div>
                                <div className={"col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("last_name")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ lastName || EMPTY_VALUES.DEFAULT }</p>
                                </div>

                                <div className={"col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("email")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ email }</p>
                                </div>

                                <div className={"col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("telephone")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ phoneNumber }</p>
                                </div>

                                <div className={"col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("document_id")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ documentId }</p>
                                </div>
                            </div>
                        }
                    />
                </div>

                <div className={"row mt-4"}>
                    <CustomCard
                        classCard={`background-shades-50 border-radius-8px`}
                        classContent={"p-0"}
                        classes={"p-0"}
                        children={
                            <div className={"row g-3 mb-3"}>
                                <div className={"border-bottom-shades-300 p-3 d-flex align-items-center"}>
                                    <div className={"mr-2"}><IconOpenEmailCard width={24} height={24}/></div>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("visit_details")}</p>
                                </div>
                                <div className={"col-12"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("employee_information")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ employeeList?.[0]?.label || EMPTY_VALUES.DEFAULT}</p>
                                </div>
                                <div className={"col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("email")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ employeeList?.[0]?.email || EMPTY_VALUES.DEFAULT }</p>
                                </div>

                                <div className={"col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("telephone")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ employeeList?.[0]?.phone || EMPTY_VALUES.DEFAULT }</p>
                                </div>

                                <div className={"col-12"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("purpose_of_visit")}</p>
                                    <p className={"font-color-general-950 fs-14-to-13 "}>{ requestAccessItem?.requestReason }</p>
                                </div>

                                <div className={"col-12"}>
                                    <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("attached_documents")}</p>

                                    { photoPickerFile?.listFiles?.length > 0 ?
                                        <FilesGallerySummary
                                            photoPickerFile={photoPickerFile}
                                            showBtnDeleteItemGallery={false}
                                            showBtnDownload={true}
                                        /> : EMPTY_VALUES.DEFAULT
                                    }

                                </div>
                            </div>
                        }
                    />
                </div>


            </Box>
        </>
    );
};
export default RequestAccessSummary
